// AuthContext.tsx
import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../helpers/api/apiConfig'; // Import the ENDPOINTS
import { User} from '../models/types';

// Context value type
interface AuthContextType {
    currentUser: User | null;
    isAuthenticated: () => boolean;
    loginUser: (userData: User) => void;
    logoutUser: () => Promise<void>;
}

// Creating the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider Props type
interface AuthProviderProps {
    children: ReactNode;
}

// AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    // Initialize currentUser from localStorage
    const storedUserData = localStorage.getItem('currentUser');
    const initialUser = storedUserData ? JSON.parse(storedUserData) : null;
    const [currentUser, setCurrentUser] = useState<User | null>(initialUser);

    // Debug: Monitor currentUser state changes
    React.useEffect(() => {
        // Authentication state monitoring
    }, [currentUser]);

    React.useEffect(() => {
        if (!currentUser?.id) {
            return;
        }

        let cancelled = false;

        const refreshCurrentUser = async () => {
            try {
                const [userResponse, companiesResponse] = await Promise.allSettled([
                    axios.get(ENDPOINTS.GET_USER),
                    axios.get(ENDPOINTS.GET_COMPANIES),
                ]);

                if (cancelled || userResponse.status !== 'fulfilled') {
                    return;
                }

                const refreshedUser = userResponse.value.data || {};
                const refreshedCompanies = companiesResponse.status === 'fulfilled'
                    ? companiesResponse.value.data
                    : null;

                setCurrentUser((previousUser) => {
                    if (!previousUser) {
                        return previousUser;
                    }

                    const nextUser: User = {
                        ...previousUser,
                        ...refreshedUser,
                        currentCompanyId: refreshedUser.currentCompanyId ?? previousUser.currentCompanyId,
                        currentCompanyRole: refreshedUser.currentCompanyRole ?? previousUser.currentCompanyRole,
                        // Always preserve companyMemberships from previous state — membership is the source of truth
                        companyMemberships: previousUser.companyMemberships,
                        isMaster: typeof refreshedUser.isMaster === 'boolean' ? refreshedUser.isMaster : previousUser.isMaster,
                        hasCreatedCompany: typeof refreshedUser.hasCreatedCompany === 'boolean'
                            ? refreshedUser.hasCreatedCompany
                            : previousUser.hasCreatedCompany,
                        canCreateCompany: typeof refreshedUser.canCreateCompany === 'boolean'
                            ? refreshedUser.canCreateCompany
                            : previousUser.canCreateCompany,
                        associatedCompanies: Array.isArray(refreshedCompanies)
                            ? refreshedCompanies
                            : previousUser.associatedCompanies,
                    };

                    localStorage.setItem('currentUser', JSON.stringify(nextUser));
                    return nextUser;
                });
            } catch (_error) {
                // Ignore background refresh failures and keep the cached user state.
            }
        };

        refreshCurrentUser();

        const refreshIntervalId = window.setInterval(refreshCurrentUser, 6 * 60 * 60 * 1000);
        window.addEventListener('focus', refreshCurrentUser);

        return () => {
            cancelled = true;
            window.clearInterval(refreshIntervalId);
            window.removeEventListener('focus', refreshCurrentUser);
        };
    }, [currentUser?.id]);

    // Function to determine if the user is authenticated
    const isAuthenticated = useCallback(() => {
        return !!currentUser;
    }, [currentUser]);

    // Function to simulate user login
    const loginUser = useCallback((userData: User) => {
        setCurrentUser(userData);
        localStorage.setItem('currentUser', JSON.stringify(userData)); // Save to localStorage
    }, []);

    // Function to handle user logout
    const logoutUser = useCallback(async () => {
        try {
            // Call the logout endpoint to invalidate server-side session
            await axios.post(ENDPOINTS.LOGOUT);
        } catch (error) {
            // Even if the server logout fails, we still want to clear local state
            // This ensures the user is logged out on the frontend
        } finally {
            
            // Clear local state regardless of server response
            setCurrentUser(null);
            localStorage.removeItem('currentUser');
            
            // Clear any other potential auth-related localStorage items
            // Cookie-based authentication - browser handles cleanup automatically
            localStorage.removeItem('authToken');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('csrfToken');
            
            // Clear sessionStorage as well
            sessionStorage.clear();
            
            // TEMPORARILY DISABLE AGGRESSIVE COOKIE CLEARING: It's breaking authentication
            // Clear all cookies (more aggressive approach)
            // document.cookie.split(";").forEach(function(c) { 
            //     document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
            // });
            
            // ADDITIONAL: Clear cookies for the specific domain
            // const domain = window.location.hostname;
            // document.cookie.split(";").forEach(function(c) { 
            //     document.cookie = c.replace(/^ +/, "").replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/;domain=${domain}`); 
            //     document.cookie = c.replace(/^ +/, "").replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/;domain=.${domain}`); 
            // });
            
            // Force a small delay to ensure all clearing operations complete
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // TEMPORARILY DISABLE PAGE RELOAD: The aggressive cookie clearing is breaking login
            // console.log('🔓 LOGOUT: Forcing page reload to reset server session...');
            // window.location.href = '/login';
            // return; // Exit early since we're reloading
        }
    }, []);

    const contextValue = useMemo(() => ({
        currentUser,
        isAuthenticated,
        loginUser,
        logoutUser,
    }), [currentUser, isAuthenticated, loginUser, logoutUser]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
